import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertCircle,
  Check,
  ChevronDown,
  Circle,
  Info,
  Loader2,
  Plus,
  X,
} from "lucide-react";
import { AppConfigKey, AppConfigSnapshot, emptyKeyRow } from "@/services/appConfigApi";
import {
  useAppConfigTags,
  useAvailableTags,
  useSaveAppConfig,
} from "@/hooks/useAppConfigurations";
import { AppConfigKeyRow } from "./AppConfigKeyRow";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import { TagIcon } from "@/components/tags/TagIcon";
import {
  validateConfigName,
  validateIdentifier,
  findDuplicateKeyRowIds,
  WIZARD_VALIDATION_MESSAGES,
} from "@/services/wizardValidation";

interface AppConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appName: string;
  bundleId: string;
  /** Pass an existing snapshot to open the dialog in edit mode. */
  existing?: AppConfigSnapshot | null;
  /**
   * Pass a snapshot to pre-fill the form WITHOUT linking to its policy id —
   * used for the "Duplicate" action which creates a brand new policy seeded
   * from another one's keys.
   */
  seed?: AppConfigSnapshot | null;
}

interface FormValue {
  configName: string;
  keys: AppConfigKey[];
  tagIds: string[];
}

interface ValidationErrors {
  configName?: string;
  keys?: { rowId: string; key?: string; value?: string }[];
  tags?: string;
  /** Top-level message rendered in the Alert slot. */
  summary?: string;
}

/**
 * Validate a row's *value* against its declared type. Mirrors the coercion
 * logic in `coerceValue` so the user sees the same errors inline that they
 * would otherwise hit at save time.
 */
function validateRowValue(row: AppConfigKey): string | undefined {
  if (!row.key.trim()) return undefined;
  if (row.type === "integer") {
    const trimmed = row.value.trim();
    if (trimmed === "") return "Must be a whole number.";
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return "Must be a whole number.";
    if (!Number.isInteger(n)) return "Integers must not have decimals.";
    return undefined;
  }
  if (row.type === "array") {
    const items = row.value
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    if (items.length === 0) {
      return "Add at least one value (comma-separated).";
    }
    return undefined;
  }
  return undefined;
}

/**
 * Apply the shared wizard validation contract so this in-app dialog uses the
 * exact same key pattern, length cap, forbidden-char list, and duplicate
 * wording as the standalone AppConfigWizard.
 */
function validate(value: FormValue): ValidationErrors {
  const errors: ValidationErrors = {};

  const nameErr = validateConfigName(value.configName);
  if (nameErr) errors.configName = nameErr;

  const dupeRowIds = findDuplicateKeyRowIds(value.keys);
  const rowErrors: NonNullable<ValidationErrors["keys"]> = [];
  let hasAnyKey = false;

  for (const row of value.keys) {
    const k = row.key.trim();
    const rowErr: { rowId: string; key?: string; value?: string } = {
      rowId: row.rowId,
    };
    if (k) {
      hasAnyKey = true;
      const keyErr = validateIdentifier(k);
      if (keyErr) {
        rowErr.key = keyErr;
      } else if (dupeRowIds.has(row.rowId)) {
        rowErr.key = WIZARD_VALIDATION_MESSAGES.keyDuplicate;
      }
      const valueErr = validateRowValue(row);
      if (valueErr) rowErr.value = valueErr;
    }
    if (rowErr.key || rowErr.value) rowErrors.push(rowErr);
  }

  if (!hasAnyKey) {
    errors.summary = WIZARD_VALIDATION_MESSAGES.noKeys;
  } else if (rowErrors.length > 0) {
    errors.keys = rowErrors;
    errors.summary = WIZARD_VALIDATION_MESSAGES.fixHighlightedRows;
  }

  if (value.tagIds.length === 0) {
    errors.tags = WIZARD_VALIDATION_MESSAGES.noTags;
    if (!errors.summary) errors.summary = errors.tags;
  }

  return errors;
}


export const AppConfigDialog = ({
  open,
  onOpenChange,
  appName,
  bundleId,
  existing,
  seed,
}: AppConfigDialogProps) => {
  const isEdit = !!existing;
  const tagsQuery = useAvailableTags();
  // Edit mode pre-loads the policy's current tags. Duplicate seeds tags from
  // the source snapshot (if its tags have already been fetched elsewhere they
  // will hydrate via React Query cache).
  const existingTagsQuery = useAppConfigTags(existing?.policyId);
  const seedTagsQuery = useAppConfigTags(seed?.policyId);
  const save = useSaveAppConfig(bundleId);

  // Build the saved baseline. In edit mode the tag baseline is filled in
  // once the existing-tags query resolves. In duplicate mode the baseline is
  // empty (so the dialog reports unsaved changes immediately) but the form's
  // initial value is seeded.
  const source = existing ?? seed ?? null;
  const sourceTags = existing ? existingTagsQuery.data : seedTagsQuery.data;
  const baseline = useMemo<FormValue>(() => {
    const keys = source?.keys?.length
      ? source.keys.map((k) => ({ ...k }))
      : [emptyKeyRow()];
    const seedConfigName =
      existing?.configName ??
      (seed?.configName ? `${seed.configName} (copy)` : "");
    return {
      configName: seedConfigName,
      keys,
      tagIds: (sourceTags ?? []).map((t) => t.id).sort(),
    };
  }, [source, existing, seed, sourceTags]);

  const [value, setValue] = useState<FormValue>(baseline);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  // Track whether the user has attempted to save at least once. Until then we
  // suppress the top-level summary alert so the dialog doesn't shout about
  // "missing tags" or "add a key" the moment it opens — but per-row errors
  // still render inline as soon as a value goes invalid.
  const [saveAttempted, setSaveAttempted] = useState(false);

  // Live validation — recomputed on every change so users see issues as they
  // type. The full result is also what we re-check on Save.
  const liveErrors = useMemo(() => validate(value), [value]);
  const errors: ValidationErrors = saveAttempted
    ? liveErrors
    : { keys: liveErrors.keys };

  // Reset form whenever the dialog opens (or the baseline changes because
  // the existing-tags query just resolved).
  useEffect(() => {
    if (open) {
      setValue(baseline);
      setSaveAttempted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, baseline]);

  const guard = useUnsavedChangesGuard({
    open,
    current: value,
    baseline,
    onClose: () => onOpenChange(false),
    onDiscard: () => {
      setValue(baseline);
      setSaveAttempted(false);
    },
    surface: "AppConfigDialog",
    isSaving: save.isPending,
  });

  const updateKeyAt = (rowId: string, next: AppConfigKey) => {
    setValue((v) => ({
      ...v,
      keys: v.keys.map((k) => (k.rowId === rowId ? next : k)),
    }));
  };

  const removeKeyAt = (rowId: string) => {
    setValue((v) => ({
      ...v,
      keys: v.keys.filter((k) => k.rowId !== rowId),
    }));
  };

  const addKey = () => {
    setValue((v) => ({ ...v, keys: [...v.keys, emptyKeyRow()] }));
  };

  const toggleTag = (tagId: string) => {
    setValue((v) => {
      const has = v.tagIds.includes(tagId);
      const nextIds = has
        ? v.tagIds.filter((id) => id !== tagId)
        : [...v.tagIds, tagId];
      return { ...v, tagIds: nextIds.sort() };
    });
  };

  const removeTag = (tagId: string) => {
    setValue((v) => ({ ...v, tagIds: v.tagIds.filter((id) => id !== tagId) }));
  };

  const handleSave = async () => {
    setSaveAttempted(true);
    const v = validate(value);
    if (v.summary || v.configName) return;

    try {
      await save.mutateAsync({
        existingPolicyId: existing?.policyId,
        appName,
        bundleId,
        configName: value.configName.trim(),
        keys: value.keys.filter((k) => k.key.trim().length > 0),
        tagIds: value.tagIds,
        previousTagIds: baseline.tagIds,
      });
      guard.markClean();
      onOpenChange(false);
    } catch {
      // Toast already surfaced by the hook; keep the dialog open.
    }
  };

  const availableTags = tagsQuery.data ?? [];
  const selectedTags = availableTags.filter((t) => value.tagIds.includes(t.id));
  const rowErrorById = new Map(
    (errors.keys ?? []).map((e) => [e.rowId, e]),
  );

  return (
    <>
      <Dialog open={open} onOpenChange={guard.requestClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between gap-3">
              <DialogTitle>App Configuration — {appName}</DialogTitle>
              {guard.isDirty && (
                <Badge
                  variant="outline"
                  className="border-warning text-warning gap-1.5 font-normal shrink-0"
                >
                  <Circle className="h-2 w-2 fill-warning text-warning" />
                  Unsaved changes
                </Badge>
              )}
            </div>
            <DialogDescription>
              Push key-value settings to this app on managed devices. The app
              reads these from <code className="font-mono">NSUserDefaults</code> at launch.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-6 pr-2">

              {errors.summary && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Can't save yet</AlertTitle>
                  <AlertDescription>{errors.summary}</AlertDescription>
                </Alert>
              )}

              {/* Section 1 — Configuration name */}
              <div className="space-y-2">
                <Label htmlFor="app-config-name">Configuration Name</Label>
                <Input
                  id="app-config-name"
                  value={value.configName}
                  onChange={(e) =>
                    setValue((v) => ({ ...v, configName: e.target.value }))
                  }
                  placeholder="e.g. Production, Staging, EU Region"
                  aria-invalid={!!errors.configName || undefined}
                  className={errors.configName ? "border-destructive" : undefined}
                />
                {errors.configName && (
                  <p className="text-xs text-destructive">{errors.configName}</p>
                )}
              </div>

              {/* Section 2 — Key/Value editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Configuration Keys</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addKey}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Add Key
                  </Button>
                </div>
                <div className="space-y-2">
                  {value.keys.length === 0 ? (
                    <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
                      No keys yet. Click <strong>Add Key</strong> to start.
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] gap-2 px-1 text-xs font-medium text-muted-foreground">
                        <div>Key</div>
                        <div>Type</div>
                        <div>Value</div>
                        <div className="w-10" aria-hidden="true" />
                      </div>
                      {value.keys.map((row) => {
                        const rowErr = rowErrorById.get(row.rowId);
                        return (
                          <div key={row.rowId} className="space-y-1">
                            <AppConfigKeyRow
                              row={row}
                              onChange={(next) => updateKeyAt(row.rowId, next)}
                              onRemove={() => removeKeyAt(row.rowId)}
                              invalidKey={!!rowErr?.key}
                              invalidValue={!!rowErr?.value}
                            />
                            {(rowErr?.key || rowErr?.value) && (
                              <div className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] gap-2 px-1">
                                <p className="text-xs text-destructive">
                                  {rowErr?.key}
                                </p>
                                <span aria-hidden="true" />
                                <p className="text-xs text-destructive">
                                  {rowErr?.value}
                                </p>
                                <span aria-hidden="true" className="w-10" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" />
                  These values are pushed to the app's managed configuration
                  dictionary (NSUserDefaults). The app developer defines which
                  keys are supported.
                </p>
              </div>

              {/* Section 3 — Deployment scope */}
              <div className="space-y-2">
                <Label>Deploy to</Label>
                <Popover open={tagPickerOpen} onOpenChange={setTagPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={tagPickerOpen}
                      className="w-full justify-between font-normal"
                    >
                      <span className="truncate">
                        {selectedTags.length === 0
                          ? "Select tags…"
                          : `${selectedTags.length} tag${selectedTags.length === 1 ? "" : "s"} selected`}
                      </span>
                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command>
                      <CommandInput placeholder="Search tags…" />
                      <CommandList>
                        <CommandEmpty>
                          {tagsQuery.isLoading ? "Loading tags…" : "No tags found."}
                        </CommandEmpty>
                        <CommandGroup>
                          {availableTags.map((tag) => {
                            const checked = value.tagIds.includes(tag.id);
                            return (
                              <CommandItem
                                key={tag.id}
                                value={tag.name}
                                onSelect={() => toggleTag(tag.id)}
                                className="gap-2"
                              >
                                <div
                                  className={`flex h-4 w-4 items-center justify-center rounded-sm border ${checked ? "border-primary bg-primary text-primary-foreground" : "border-input"}`}
                                  aria-hidden="true"
                                >
                                  {checked && <Check className="h-3 w-3" />}
                                </div>
                                <TagIcon
                                  iconName={tag.icon}
                                  color={tag.color}
                                  size={10}
                                />
                                <span className="flex-1 truncate">{tag.name}</span>
                                <span className="text-xs text-muted-foreground capitalize">
                                  {tag.type}
                                </span>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {selectedTags.map((tag) => (
                      <Badge
                        key={tag.id}
                        variant="secondary"
                        className="gap-1 pr-1"
                        style={{
                          backgroundColor: `${tag.color}20`,
                          borderColor: tag.color,
                          color: tag.color,
                        }}
                      >
                        <TagIcon iconName={tag.icon} color={tag.color} size={8} />
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => removeTag(tag.id)}
                          className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"
                          aria-label={`Remove ${tag.name}`}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
                {errors.tags && !errors.summary && (
                  <p className="text-xs text-destructive">{errors.tags}</p>
                )}
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setValue(baseline);
                setSaveAttempted(false);
                onOpenChange(false);
              }}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={save.isPending || (isEdit && !guard.isDirty)}
              title={isEdit && !guard.isDirty ? "No changes to save" : undefined}
            >
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={guard.confirmOpen}
        onCancel={guard.cancelDiscard}
        onDiscard={guard.confirmDiscard}
        isSaving={guard.isSaving}
      />
    </>
  );
};
